import { useRef, useState } from 'react';
import { AlarmEvent, CountData, Events, TimelineDataWithLoadingState } from '@interfaces/timeline';
import { getUTCFormatOfLocalTime, controlAPICall } from '@helpers';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { getMachineAlarmEvent, getMachineEventTimelineByBatchId } from '@reducers/machines';
import { productionBatchesSearch } from '@reducers/search';
import { useBatchTimelineSelectors } from './useBatchTimelineSelectors';
import { useRealtimeTimelineUpdates } from '@components/machines/components/timeline/hooks/useRealtimeTimelineUpdates';
import { processTimelineResponse } from '@components/machines/components/timeline/helpers/mergeTimelineEvents';
import { EMPTY_EVENTS } from '@components/machines/components/timeline/constants';

export interface UseBatchTimelineViewModelParams {
  productionBatchStartDateForDisplay: string | null;
  productionBatchEndDateForDisplay: string | null;
}

export const useBatchTimelineViewModel = ({
  productionBatchStartDateForDisplay,
  productionBatchEndDateForDisplay,
}: UseBatchTimelineViewModelParams) => {
  const dispatch = useAppDispatch();

  const { batch } = useBatchTimelineSelectors();
  const productionBatchId = batch?.data?.productionBatchId;
  const batchNcFileId = batch?.data?.ncFileId;

  const { fetchTimelineWithRealtime } = useRealtimeTimelineUpdates();

  const abortControllerEvents = useRef<AbortController>();
  const abortControllerAlarmEvent = useRef<AbortController>();
  const abortControllerCountsSearch = useRef<AbortController>();
  const cancelRealtimeRef = useRef<(() => void) | null>(null);

  const [events, setEvents] = useState<Events>(EMPTY_EVENTS);

  const [alarmEventGroups, setAlarmEventGroups] = useState<TimelineDataWithLoadingState<AlarmEvent[][]>>({
    loaded: false,
    data: [],
  });

  const [countsData, setCountsData] = useState<TimelineDataWithLoadingState<CountData[]>>({
    loaded: false,
    data: [],
  });

  const eventsRef = useRef(events);
  eventsRef.current = events;

  const getTimeLine = () => {
    if (productionBatchId == null) return;

    setEvents(EMPTY_EVENTS);

    cancelRealtimeRef.current?.();
    if (abortControllerEvents.current) {
      abortControllerEvents.current.abort();
    }
    abortControllerEvents.current = new AbortController();

    cancelRealtimeRef.current = fetchTimelineWithRealtime({
      productionBatchId,
      thunk: getMachineEventTimelineByBatchId(
        { productionBatchId, useAsyncFlowForRealtimeQuery: true, pagination: { pageNumber: 1, pageSize: 1000, includeCount: true } },
        abortControllerEvents.current.signal
      ),
      processResponse: processTimelineResponse,
      onInitialData: (data) => {
        setEvents({ loaded: true, ...data });
      },
      onRealtimeData: (data) => {
        setEvents({ loaded: true, ...data });
      },
      getCurrentEvents: () => eventsRef.current,
    });
  };

  const getCountData = async () => {
    if (productionBatchId == null) return;

    if (abortControllerCountsSearch.current) {
      abortControllerCountsSearch.current.abort();
    }

    abortControllerCountsSearch.current = new AbortController();
    const signal = abortControllerCountsSearch.current.signal;

    setCountsData({ loaded: false, data: [] });

    try {
      const countsData = await dispatch(
        productionBatchesSearch({
          data: {},
          filter: {
            oDataFilter: `ProductionBatchId eq ${productionBatchId}`,
          },
          select: ['QuantityEvents'],
          resetHistory: true,
          ignoreHistory: true,
          signal,
        })
      );

      setCountsData({
        loaded: true,
        data: countsData?.searchResult?.[0]?.quantityEvents?.map((item: any) => ({ ...item, ncFileId: batchNcFileId })) ?? [],
      });
    } catch (error) {
      const e = error as Error;
      if (e.name !== 'AbortError') {
        console.error('Failed to fetch count data:', error);
      }
      setCountsData({ loaded: true, data: [] });
    }
  };

  const getAlarmEvent = async ({ start, end }: { start: string; end: string }) => {
    if (productionBatchId == null || start == null || end == null) return;

    setAlarmEventGroups({ loaded: false, data: [] });

    const data = await controlAPICall(
      (params: any, signal?: AbortSignal) => dispatch(getMachineAlarmEvent(params, signal)),
      {
        productionBatchId,
        start: getUTCFormatOfLocalTime(start),
        end: getUTCFormatOfLocalTime(end),
      },
      abortControllerAlarmEvent
    );

    setAlarmEventGroups({ loaded: true, data });
  };

  const fetchData = async (params?: { start?: string; end?: string }) => {
    const { start = productionBatchStartDateForDisplay, end = productionBatchEndDateForDisplay } = params || {};

    if (productionBatchId && start && end) {
      const results = await Promise.allSettled([getTimeLine(), getAlarmEvent({ start, end }), getCountData()]);

      // Log any rejected promises for debugging
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const requestNames = ['getTimeLine', 'getAlarmEvent', 'getCountData'];
          console.error(`${requestNames[index]} failed:`, result.reason);
        }
      });
    }
  };

  return {
    fetchData,
    abortControllerEvents,
    abortControllerAlarmEvent,
    abortControllerCountsSearch,
    events,
    alarmEventGroups,
    countsData,
    productionBatchId,
    batch,
  };
};
